<template>
  <div
    v-if="squareCardDetails"
    data-cy="stored-card-details"
    class="stored-credit-card-details"
    :class="`card-display-${size}`"
  >
    <template v-if="cardisNew">
      <div class="new-card-entry">
        Enter new card
      </div>
    </template>
    <template v-if="!cardisNew">
      <div
        class="card-logo"
      >
        <img
          :src="cardLogoImage"
          :class="`card-${cardLogo}`"
        >
      </div>
      <div class="details-wrapper">
        <div class="card-description">
          <div class="card-info">
            {{ cardNumber }}
          </div>
          <div
            v-if="!noExpiryDate"
            class="card-expiry-date"
          >
            <strong>Exp:</strong> {{ expDate }}
          </div>
        </div>
        <div class="card-options">
          <slot />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'square-payments-storedcard-display',
  props: {
    account: {
      type: Object,
      required: true
    },
    cardDetails: {
      type: [String, Object],
      required: false,
      default: () => ({ id: 'new', card: { type: 'new' } })
    },
    size: {
      type: String,
      required: false,
      default: 'sm'
    },
    noExpiryDate: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function() {
    return {
      loading: true,
      squareCardDetails: null
    }
  },
  computed: {
    cardNumber: function() {
      if (!this.loading) {
        const bin = this.squareCardDetails.bin || ''
        const last4 = this.squareCardDetails.last_4 || ''

        // Format as XXXX XXXX XXXX XXXX (groups of 4)
        const cardNumber = bin + 'X'.repeat(6) + last4
        return cardNumber.replace(/(.{4})/g, '$1 ').trim()
      }
      else {
        return ' -- unknown --'
      }
    },

    cardLogoImage: function() {
      switch (this.cardLogo) {
      case 'visa': return 'https://static.aspedia.net/images/payment/visa.svg'
      case 'mastercard': return 'https://static.aspedia.net/images/payment/mastercard.svg'
      case 'amex': return 'https://static.aspedia.net/images/payment/amex.svg'
      default: return 'https://static.aspedia.net/images/payment/default.svg'
      }
    },
    cardType: function() {
      return this.loading ? 'default' : this.squareCardDetails.card_brand
    },
    cardisNew: function() {
      return false
    },
    cardLogo: function() {
      return this.loading ? 'default' : this.cardType.toLowerCase()
    },
    expDate: function() {
      if (!this.loading) {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec']
        let month = this.squareCardDetails.exp_month
        month = months[parseInt(month) - 1]
        const year = this.squareCardDetails.exp_year
        return `${month} ${year}`
      }
      else {
        return ''
      }
    }
  },
  mounted: function() {
    if (typeof this.cardDetails === 'string') {
      this.loadCardDetails(this.cardDetails)
    }
    else if (!Object.prototype.hasOwnProperty.call(this.cardDetails, 'fingerprint')) {
      this.loadCardDetails(this.cardDetails.token)
    }
    else {
      this.squareCardDetails = this.cardDetails
      this.loading = false
    }
  },
  methods: {
    loadCardDetails: function(cardToken) {
      this.loading = true
      this.$emit('loading', true)
      this.loadingSquareCards = true
      this.$store.dispatch('square/loadCustomerCards', this.account)
        .then(cardDetails => {
          if (cardDetails && Array.isArray(cardDetails)) {
            this.squareCardDetails = cardDetails.find(card => card.id === cardToken)
          }
          if (!this.squareCardDetails) {
            this.emit('reset', true)
            this.squareCardDetails = { id: 'new', card: { type: 'new' } }
          }
        })
        .catch(err => {
          console.error(`Square :: square-payments-storedcard-display Error -- Loading Stored payment Details -- ${err.reason}.`)
          this.$emit('error', err)
        })
        .finally(() => {
          this.loading = false
          this.$emit('loading', false)
        })
    }
  }
}
</script>

<style lang="scss" scoped>
  .card-logo {
    flex-shrink: 1;
  }

  .stored-credit-card-details {
    position: relative; // Add relative positioning to enable absolute positioning of button

    .details-wrapper { // General rule for all .details-wrapper elements
      display: flex;
      flex-direction: column;
      align-items: flex-start; // Align card description and options to the left
      flex-grow: 1;
      // Padding is handled by size-specific .details-wrapper rules (e.g., in .card-display-md)
    }

    .card-description { // From previous edit, already good
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      .card-info { // From previous edit, already good
        font-size: 1.2em; // Maintains increased font size and prominence
        font-weight: bold;
      }

      .card-expiry-date { // From previous edit, already good
        margin-left: 0;
        margin-top: 0.4rem; // Space between card number and expiry date
      }
    }
  }

  .card-display-sm {
    .card-logo {
      display: flex;
      flex-direction: column;
      justify-content: center;
      img {
        width: 60px;
      }
    }
    display: flex;
    flex-direction: row;
    align-items: center;

    .details-wrapper {
      display: flex;
      flex-direction: row; // Change to row to put description and options side by side
      align-items: center;
      justify-content: space-between; // This will push the button to the right
      width: 100%; // Ensure wrapper takes full width
    }

    .card-description {
      display: flex;
      flex-direction: row; // Changed to row to place card number and expiry side by side
      align-items: center; // Center align the items
      gap: 1rem; // Add space between card number and expiry date

      .card-expiry-date {
        margin-left: 0;
        margin-top: 0; // Remove top margin since they're now side by side

        strong {
          display: none; // Hide the "Exp:" label for sm size
        }
      }
    }

    .card-options {
      position: static; // Override absolute positioning for sm size
      margin-top: 0; // Remove top margin for inline layout
      margin-left: 0;
      margin-right: 0.5rem; // Add right margin to match spacing balance
      width: auto; // Allow button to size naturally

      // Reduce button padding to match text size
      button {
        padding: 0.2rem 0.5rem;
        font-size: 0.9em;
      }
    }
  }

  .card-display-md {
    .card-logo {
      img {
        width: 120px;
      }
      height: auto;
      display: flex;
      flex-direction: row;
      align-items: center;
    }
    display: flex;
    flex-direction: row;
    align-items: center;

    padding: 0.4rem 0.4rem;

    .details-wrapper {
      padding: 0.8rem 1.5rem; // Existing padding
      // Inherits flex-direction: column from general rule
    }

    .card-description {
      display: flex;
      flex-direction: row; // Changed to row to place card number and expiry side by side
      align-items: center; // Center align the items
      gap: 1rem; // Add space between card number and expiry date

      .card-expiry-date {
        margin-left: 0;
        margin-top: 0; // Remove top margin since they're now side by side
      }
    }
  }

  .card-display-lg {
    .card-logo img {
      width: 220px;
    }
    display: flex;
    flex-direction: row; // Changed back to row layout

    padding: 0.8rem 0.8rem;

    .details-wrapper {
      padding: 0.8rem 1.5rem; // Existing padding
      // Inherits flex-direction: column from general rule
    }
  }

  .card-display-xl {
    .card-logo img {
      width: 440px;
    }
    display: flex;
    flex-direction: column; // Changed from row to column to stack logo above details

    padding: 1.2rem 1.2rem;

    .details-wrapper {
      padding: 0.5rem 0.8rem; // Existing padding
      // Inherits flex-direction: column from general rule
      .card-description { // Existing rule for XL
        font-size: 2.3em;
      }
    }
  }

  .card-options { // Position button in lower right for all sizes except sm
    position: absolute;
    bottom: 0.5rem;
    right: 0.5rem;
    margin-top: 0;
    margin-left: 0;
    width: auto;

    button {
      padding: 0.3rem 0.6rem;
      font-size: 0.9em;
    }
  }

  .card-options:empty {
    display: none;
  }

  .new-card-entry {
    margin-left: 1.2rem;
  }
</style>
